/**
 * The full map's key: only what is on the map right now (you, your car, your route, live markers, the shops the
 * borough has), in the scheme's order (markerScheme.ts: the goal first, the shops last), with the same
 * glyphs, colours and shapes as the map — in five groups a phone can read (You & your car · Jobs & contacts ·
 * Shops & garages · Police · Online players), four of which can be switched off to thin the map (never the
 * goal, your route or your car). Pure; tested in mapView.test.ts.
 */
import type { PoiKind } from '../world/types';
import type { MapMarker, MarkerKind } from './uiTypes';

/** One key line: a marker swatch (kind + glyph), what it means, and which markers it lights up when tapped. */
export interface LegendItem {
  kind: MarkerKind | 'you';
  glyph?: string;
  label: string;
}

/** The key's groups (docs/UI.md "The full map"): what the lines are sorted under, and what a switch hides. */
export type LegendGroup = 'you' | 'jobs' | 'shops' | 'police' | 'online';
export const GROUP_ORDER: readonly LegendGroup[] = ['you', 'jobs', 'shops', 'police', 'online'];
export const GROUP_LABEL: Record<LegendGroup, string> = {
  you: 'You and your car',
  jobs: 'Jobs and contacts',
  shops: 'Shops and garages',
  police: 'Police',
  online: 'Online players',
};
/** Groups with a switch; "You and your car" is always on the map. */
export const TOGGLEABLE: readonly LegendGroup[] = ['jobs', 'shops', 'police', 'online'];
export const GROUP_OF: Record<LegendItem['kind'], LegendGroup> = {
  you: 'you',
  car: 'you',
  mark: 'you',
  next: 'you',
  objective: 'jobs',
  target: 'jobs',
  friend: 'jobs',
  event: 'jobs',
  hazard: 'jobs',
  poi: 'shops',
  police: 'police',
  crew: 'online',
  gang: 'online',
  player: 'online',
};
/** Kinds a switch never hides: the goal, its parts, your route, the suggestion and your car stay on the map. */
const ALWAYS_SHOWN: ReadonlySet<MarkerKind> = new Set<MarkerKind>(['objective', 'target', 'mark', 'next', 'car']);

/** The marker kinds the hidden groups take off the map (the minimap and the full map alike). */
export function hiddenKinds(hidden: ReadonlySet<LegendGroup>): Set<MarkerKind> {
  const out = new Set<MarkerKind>();
  for (const k of Object.keys(GROUP_OF) as LegendItem['kind'][]) {
    if (k === 'you' || ALWAYS_SHOWN.has(k)) continue;
    if (hidden.has(GROUP_OF[k])) out.add(k);
  }
  return out;
}

/** The hidden groups as saved (localStorage `bh-map-hide`) and read back; unknown names are dropped. */
export function parseHidden(json: string | null): Set<LegendGroup> {
  const out = new Set<LegendGroup>();
  if (!json) return out;
  try {
    const arr = JSON.parse(json) as unknown;
    if (Array.isArray(arr)) for (const g of arr) if (TOGGLEABLE.includes(g as LegendGroup)) out.add(g as LegendGroup);
  } catch {
    /* not ours */
  }
  return out;
}

export const serializeHidden = (hidden: ReadonlySet<LegendGroup>): string => JSON.stringify(GROUP_ORDER.filter((g) => hidden.has(g)));

/** POI kinds the map draws (minimap.ts POI_GLYPH) with their key line. */
export const POI_KINDS: [PoiKind, string, string][] = [
  ['garage', 'G', 'Garage — repairs'],
  ['resprayShop', 'R', 'Respray shop — new paint'],
  ['lockup', 'L', 'Lockup'],
  ['safehouse', 'S', 'Safehouse'],
  ['policeStation', '!', 'Police station'],
];
const POI_LEGEND: Record<string, string> = { F: 'Sid’s back room — weapons', '£': 'Dropped cash', P: 'Premises', B: 'Bank', N: 'Night Freight pier' };
const FRIEND_GLYPHS: Record<string, string> = {
  M: 'Mags — story jobs',
  '☎': 'A ringing phone box',
  '£': 'Halloran — export orders',
  '★': 'Rita — street races',
  C: 'Minicab shift',
  W: 'Des — the workshop',
  H: 'Half-brick — mayhem',
};
const EVENT_GLYPHS: Record<string, string> = { '£': 'A buyer in town', X: 'Turf contest' };
/** Key lines in the scheme's order: you and yours first, then what you're doing, then people, then places. */
const ORDER: Record<LegendItem['kind'], number> = { you: 0, car: 1, objective: 2, mark: 3, target: 4, next: 5, event: 6, crew: 7, gang: 8, player: 9, friend: 10, hazard: 11, police: 12, poi: 13 };

export function buildLegend(markers: readonly MapMarker[], poiKinds: ReadonlySet<PoiKind>, mark: { label: string } | null): LegendItem[] {
  const out: LegendItem[] = [{ kind: 'you', label: 'You' }];
  if (mark) out.push({ kind: 'mark', label: `Your route — ${mark.label}` });
  const seen = new Set<string>();
  const add = (kind: LegendItem['kind'], glyph: string | undefined, label: string): void => {
    const key = `${kind}:${glyph ?? ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(glyph ? { kind, glyph, label } : { kind, label });
  };
  for (const m of markers) {
    if (m.area) continue;
    if (m.kind === 'objective') add('objective', undefined, m.label ? `The job — ${m.label}` : 'The job — go here');
    else if (m.kind === 'car') add('car', m.glyph, m.label ?? (m.glyph === '★' ? 'Your motor' : 'Your last car'));
    else if (m.kind === 'next') add('next', undefined, m.label ? `Suggested next — ${m.label}` : 'Suggested next');
    else if (m.kind === 'friend') add('friend', m.glyph, FRIEND_GLYPHS[m.glyph ?? ''] ?? m.label ?? 'Someone with work');
    else if (m.kind === 'target') add('target', undefined, 'Part of the job');
    else if (m.kind === 'event') add('event', m.glyph, EVENT_GLYPHS[m.glyph ?? ''] ?? 'Something on (events)');
    else if (m.kind === 'crew') add('crew', undefined, 'Your crew');
    else if (m.kind === 'gang') add('gang', undefined, 'Your gang');
    else if (m.kind === 'player') add('player', undefined, 'Other players (orange: wanted)');
    else if (m.kind === 'hazard') add('hazard', undefined, 'Trouble');
    else if (m.kind === 'police') add('police', undefined, 'Police');
    else if (m.kind === 'poi' && m.glyph && POI_LEGEND[m.glyph]) add('poi', m.glyph, POI_LEGEND[m.glyph]!);
  }
  for (const [kind, glyph, label] of POI_KINDS) if (poiKinds.has(kind)) add('poi', glyph, label);
  // Stable sort: the scheme's order, first-seen within a kind.
  return out.map((it, i) => ({ it, i })).sort((a, b) => ORDER[a.it.kind] - ORDER[b.it.kind] || a.i - b.i).map((x) => x.it);
}

/** The key's lines under their group headings, in GROUP_ORDER; groups with nothing on the map are left out. */
export function groupLegend(items: readonly LegendItem[]): { group: LegendGroup; label: string; items: LegendItem[] }[] {
  const by = new Map<LegendGroup, LegendItem[]>();
  for (const it of items) {
    const g = GROUP_OF[it.kind];
    const list = by.get(g) ?? [];
    list.push(it);
    by.set(g, list);
  }
  return GROUP_ORDER.filter((g) => by.has(g)).map((g) => ({ group: g, label: GROUP_LABEL[g], items: by.get(g)! }));
}

/** A stable fingerprint of a key, so the list is only rebuilt when what's on the map changes. */
export function legendKey(items: readonly LegendItem[]): string {
  let s = '';
  for (const it of items) s += `${it.kind}|${it.glyph ?? ''}|${it.label};`;
  return s;
}

/** Does `m` belong to the key line `it` (the markers a tap on that line flashes)? */
export function legendMatches(it: { kind: LegendItem['kind']; glyph?: string }, m: MapMarker): boolean {
  if (m.area || m.kind !== it.kind) return false;
  return it.kind === 'friend' || it.kind === 'poi' || it.kind === 'event' || it.kind === 'car' ? (m.glyph ?? '') === (it.glyph ?? '') : true;
}
